import { useCallback, useMemo, useEffect, FC, RefObject } from 'react';
import { useHookstate } from '@hookstate/core';
import {
    PartWinners,
    WinnerModal as WinnerModalComponent,
} from '../../components/WinnerModal';
import { useFormatCurrency } from '../../hooks/use-format-currency';
import { useFocusChat } from '../../hooks/use-focus-chat';
import { useGameType } from '../../hooks/use-game-type';
import {
    current as currentSate,
    meta as metaState,
    winners as winnersState,
    jackpotWinner as jackpotWinnerState,
} from '../../stores/backend';
import { chatMeta } from '../../stores/chat';
import { RoundedEmojiInputRef } from '../../components/RoundedEmojiInput';
import { isSuspended } from '../../lib/suspension';

type WinnerModalProps = {
    inputRef: RefObject<RoundedEmojiInputRef>;
};

export const WINNER_MODAL_CLOSE_DELAY = 15000;

export const WinnerModal: FC<WinnerModalProps> = ({ inputRef }) => {
    const meta = useHookstate(metaState);
    const current = useHookstate(currentSate);
    const winners = useHookstate(winnersState);
    const jackpotWinner = useHookstate(jackpotWinnerState);
    const $chatMeta = useHookstate(chatMeta);

    const formatCurrency = useFormatCurrency(current, meta);
    const gameType = useGameType(current);

    // Jackpot amounts are in the jackpot currency, not the game currency.
    const jackpotWinners = useMemo(() => {
        const jackpot = jackpotWinner.value;
        return (
            jackpot?.winners.map((winner) => ({
                cardId: winner.cardId,
                alias: winner.userAlias,
                amount: formatCurrency(winner.amount, jackpot.currency),
            })) ?? []
        );
    }, [jackpotWinner.value, formatCurrency]);

    const totalJackpotAmount = useMemo(
        () =>
            jackpotWinner.value
                ? formatCurrency(
                      jackpotWinner.value.amount,
                      jackpotWinner.value.currency
                  )
                : '',
        [jackpotWinner.value, formatCurrency]
    );

    const {
        partWinners,
        totalPrizes,
        lastWinner,
        consolationWinners,
        fireBallWin,
        fireBallPrize,
        fireBallPrizeType,
    } = useMemo(() => {
        const gameWinners = winners.winners.value || [];
        const partWinners: PartWinners[][] = [];

        let totalPrizes = 0;

        for (const winner of gameWinners) {
            (partWinners[winner.part - 1] =
                partWinners[winner.part - 1] || []).push({
                name: winner.userAlias,
                prize: formatCurrency(
                    winner.amount,
                    current.game_currency.value
                ),
            });
            totalPrizes += winner.amount + (winner.fireBallAmount || 0);
        }

        // Any winner flagged fireBallWin drives the room-wide Fire Ball indication.
        const fireBallWinner = gameWinners.find((winner) => winner.fireBallWin);

        return {
            partWinners,
            consolationWinners: winners.consolationWinners.value,
            lastWinner: gameWinners[gameWinners.length - 1],
            totalPrizes: formatCurrency(
                totalPrizes,
                current.game_currency.value
            ),
            fireBallWin: !!fireBallWinner,
            fireBallPrize: fireBallWinner
                ? formatCurrency(
                      fireBallWinner.fireBallAmount || 0,
                      current.game_currency.value
                  )
                : undefined,
            fireBallPrizeType: fireBallWinner?.fireBallPrizeType,
        };
    }, [
        winners.winners.value,
        winners.consolationWinners.value,
        current.game_currency.value,
        formatCurrency,
    ]);

    const onClose = useCallback(() => {
        if (isSuspended()) return;
        meta.modals.winner.set(false);
    }, [meta.modals.winner]);

    // Chat input is focused again so users are not interrupted if they are chatting with others while this modal opens.
    const focusChat = useFocusChat(inputRef, $chatMeta);

    useEffect(() => {
        if (!meta.modals.winner.value) return;
        const id = setTimeout(onClose, WINNER_MODAL_CLOSE_DELAY);
        return () => {
            clearTimeout(id);
        };
    }, [onClose, meta.modals.winner.value]);

    return (
        <WinnerModalComponent
            isOpen={meta.modals.winner.value}
            gameName={current.game_desc.value}
            gameType={gameType}
            gameId={winners.gameSessionId.value}
            winningCall={lastWinner?.winningCall}
            winningNumber={lastWinner?.winningNumber}
            partWinners={partWinners}
            consolationWinners={consolationWinners}
            jackpotWinners={jackpotWinners}
            totalJackpotAmount={totalJackpotAmount}
            totalPrizes={totalPrizes}
            gameContinued={current.game_continued.value}
            fireBallWin={fireBallWin}
            fireBallPrize={fireBallPrize}
            fireBallPrizeType={fireBallPrizeType}
            recentWinners={false}
            onClose={onClose}
            onAfterOpen={focusChat}
            loaded={true}
        />
    );
};
